/**
 * Limanlar, mesafe tablosu ve "Route & Distances" metinleri (M13b; ADR 0048). Ekran adları
 * İngilizce; Türkçe ikinci dil.
 */

const errorsEn = {
  GENERIC: 'The operation failed. Try again.',
  NETWORK: 'No connection. Check the network and try again.',
  FORBIDDEN: 'You are not allowed to do this.',
  NOT_FOUND: 'The record was not found. It may have been removed.',
  VALIDATION_FAILED: 'Some fields are invalid.',
  ROW_VERSION_CONFLICT: 'Someone else changed this record. Reopen it and try again.',
  UNKNOWN_PORT: 'The port is not in the port list.',
  INVALID_PORT_CODE: 'The port code is invalid.',
  INVALID_PORT_DISTANCE: 'Some fields are invalid.',
  INVALID_VOYAGE_PORTS: 'Some ports of the route are invalid.',
  DUPLICATE_PORT_PAIR: 'This pair already has a distance. Edit the existing row.',
  DISTANCE_SOURCE_LOWER: 'A manual or imported distance cannot be replaced by a computed one.',
  NO_SEA_ROUTE: 'No sea route was found between these ports.',
  PORT_CODE_EXISTS: 'This code is already in the port list.',
  DUPLICATE_PORT_CODE: 'A company port with this code already exists.',
  INVALID_COUNTRY: 'The first two letters of the code must be the country code.',
  INVALID_POSITION: 'The latitude or longitude is out of range.',
  INVALID_PORT_NAME: 'Enter the port name.',
  PORT_CODE_LOCKED: 'The code of a company port cannot change.',
  PORT_IN_USE: 'The port is used in the distance table; remove those distances first.',
  OFFICE_IS_WRITER: 'The distance table is maintained by the office.',
  VOYAGE_CANCELLED: 'The voyage is cancelled.',
  VOYAGE_UNKNOWN: 'The voyage was not found on this vessel.',
  SAME_PORT: 'From and To must be different ports.',
};

const errorsTr: typeof errorsEn = {
  GENERIC: 'İşlem yapılamadı. Yeniden deneyin.',
  NETWORK: 'Bağlantı yok. Ağı denetleyip yeniden deneyin.',
  FORBIDDEN: 'Bu işlem için yetkiniz yok.',
  NOT_FOUND: 'Kayıt bulunamadı. Kaldırılmış olabilir.',
  VALIDATION_FAILED: 'Bazı alanlar geçersiz.',
  ROW_VERSION_CONFLICT: 'Kaydı başka biri değiştirdi. Yeniden açıp deneyin.',
  UNKNOWN_PORT: 'Liman, liman listesinde yok.',
  INVALID_PORT_CODE: 'Liman kodu geçersiz.',
  INVALID_PORT_DISTANCE: 'Bazı alanlar geçersiz.',
  INVALID_VOYAGE_PORTS: 'Güzergâhtaki bazı limanlar geçersiz.',
  DUPLICATE_PORT_PAIR: 'Bu çiftin mesafesi zaten var. Var olan satırı düzeltin.',
  DISTANCE_SOURCE_LOWER: 'Elle girilen ya da içe aktarılan mesafe hesaplananla değiştirilemez.',
  NO_SEA_ROUTE: 'Bu limanlar arasında deniz yolu bulunamadı.',
  PORT_CODE_EXISTS: 'Bu kod liman listesinde zaten var.',
  DUPLICATE_PORT_CODE: 'Bu kodla bir şirket limanı zaten var.',
  INVALID_COUNTRY: 'Kodun ilk iki harfi ülke kodu olmalı.',
  INVALID_POSITION: 'Enlem ya da boylam sınır dışında.',
  INVALID_PORT_NAME: 'Liman adını girin.',
  PORT_CODE_LOCKED: 'Şirket limanının kodu değişmez.',
  PORT_IN_USE: 'Liman mesafe tablosunda kullanılıyor; önce o mesafeleri kaldırın.',
  OFFICE_IS_WRITER: 'Mesafe tablosunu ofis tutar.',
  VOYAGE_CANCELLED: 'Sefer iptal edilmiş.',
  VOYAGE_UNKNOWN: 'Sefer bu gemide bulunamadı.',
  SAME_PORT: 'Kalkış ve varış farklı limanlar olmalı.',
};

export const portsEn = {
  picker: {
    placeholder: 'Search port, UN/LOCODE or country',
    freeText: 'Not in the port list: kept as text',
    pickFromList: 'Pick a port from the list',
    company: 'company port',
  },
  approx: 'Approximate distances; not for navigation.',
  attribution: 'Sea routes: {{network}} · Ports: {{ports}}',
  source: {
    manual: 'Manual',
    imported: 'Imported',
    computed: 'Computed',
    estimate: 'Estimate',
  },
  route: {
    title: 'Route & Distances',
    description: 'Ports of the voyage, leg distances and a port order suggestion',
    card: 'Voyage Route',
    columns: {
      no: '#',
      port: 'Port',
      country: 'Country',
      leg: 'Leg (NM)',
      source: 'Source',
      cumulative: 'Cumulative (NM)',
      actions: 'Actions',
    },
    role: { from: 'Departure', via: 'Port call', to: 'Destination' },
    total: 'Total',
    totalDistance: 'Total Distance',
    planned: 'Planned Distance',
    difference: 'Difference',
    legs: 'Legs',
    storedLegs: 'From the distance table',
    computedLegs: 'Estimated now',
    noCode: 'Not in the port list',
    noRoute: 'No sea route',
    addPort: 'Add port call',
    add: 'Add',
    moveUp: 'Move {{port}} up',
    moveDown: 'Move {{port}} down',
    remove: 'Remove {{port}}',
    save: 'Save Route',
    saved: 'Route saved.',
    discard: 'Discard',
    unsaved: 'The route has unsaved changes.',
    suggest: 'Suggest Order',
    suggestTitle: 'Order Suggestion',
    suggestCurrent: 'Current order',
    suggestNew: 'Suggested order',
    saving: 'Shorter by {{nm}} NM',
    apply: 'Apply Order',
    applied: 'Suggested order applied. Save the route to keep it.',
    alreadyBest: 'The current order is already the shortest.',
    missingCode: 'Every port needs a code from the port list to suggest an order.',
    nothing: 'Add at least two port calls to compare orders.',
    noRouteSuggest: 'A leg has no sea route; the order cannot be compared.',
    heuristic: 'Approximate for many ports (nearest neighbour + 2-opt).',
    usePlanned: 'Use as Planned Distance',
    plannedSaved: 'Planned distance updated.',
    addToTable: 'Save to distance table',
    addedToTable: 'Saved to the distance table.',
    officeNote: 'The route is planned on board; the office sees it here.',
    loading: 'Loading port data…',
    dataError: 'Port data could not be loaded.',
    summary: 'Summary',
    legOf: '{{from}} → {{to}}',
  },
  distances: {
    title: 'Port Distance Library',
    description: "The company's port-to-port distance table",
    tabs: { distances: 'Distances', ports: 'Company Ports' },
    search: 'Search port or code',
    table: 'Distance table',
    columns: {
      from: 'From',
      to: 'To',
      distance: 'Distance (NM)',
      source: 'Source',
      updated: 'Updated',
      note: 'Note',
      actions: 'Actions',
    },
    add: 'Add Distance',
    import: 'Import CSV',
    emptyTitle: 'No distances yet',
    emptyText:
      'Distances you enter or import are kept here and are used before computed estimates.',
    emptyShip: 'The office maintains the distance table; legs are estimated on board until then.',
    noMatch: 'No distance matches the search',
    error: 'Distances could not be loaded',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
    pagination: 'Distance pages',
    actionsOf: 'Actions for {{pair}}',
    edit: 'Edit',
    history: 'History',
    remove: 'Remove',
    recompute: 'Recompute',
    removed: 'Distance removed.',
    saved: 'Distance saved.',
    dialog: {
      title: 'Add Distance',
      editTitle: 'Edit Distance',
      from: 'From',
      to: 'To',
      distance: 'Distance (NM)',
      note: 'Note',
      compute: 'Estimate',
      computed: 'Estimated: {{nm}} NM',
      saveManual: 'Save',
      saveComputed: 'Save Estimate',
      cancel: 'Cancel',
      manualHint: 'A manual distance always overrides a computed one.',
    },
    historyDialog: {
      title: 'Change History',
      empty: 'No changes recorded.',
      insert: 'Created',
      update: 'Updated',
      delete: 'Removed',
      by: 'by {{name}}',
      close: 'Close',
      fields: {
        distance_nm: 'Distance',
        source: 'Source',
        note: 'Note',
        network_source: 'Network',
        port_a: 'Port A',
        port_b: 'Port B',
        deleted_at: 'Removed',
      },
    },
    importDialog: {
      title: 'Import Distances',
      help: 'CSV with the columns from, to, distance_nm and an optional note; comma, semicolon or tab separated; the header row is optional. Port codes are UN/LOCODE or codes from the port list. Manual distances are kept.',
      file: 'CSV file',
      text: 'Rows',
      preview: 'Preview',
      apply: 'Import',
      result: '{{rows}} rows: {{inserted}} new, {{updated}} updated, {{skipped}} skipped',
      issues: 'Fix these lines and preview again:',
      line: 'Line {{line}}',
      skipped: 'Skipped lines',
      manualKept: 'Line {{line}}: manual distance kept',
      unchanged: 'Line {{line}}: unchanged',
      done: 'Import completed.',
    },
    ports: {
      add: 'Add Port',
      search: 'Search code or name',
      table: 'Company ports',
      columns: {
        code: 'Code',
        name: 'Name',
        country: 'Country',
        position: 'Position',
        euEea: 'EU / EEA',
        notes: 'Notes',
        actions: 'Actions',
      },
      emptyTitle: 'No company ports',
      emptyText: 'Add terminals, berths or anchorages that are not in the port list.',
      yes: 'Yes',
      no: 'No',
      saved: 'Port saved.',
      removed: 'Port removed.',
      actionsOf: 'Actions for {{code}}',
      dialog: {
        title: 'Add Company Port',
        editTitle: 'Edit Company Port',
        code: 'Code',
        codeHint: 'UN/LOCODE format: the first two letters are the country',
        name: 'Name',
        country: 'Country code',
        lat: 'Latitude (°)',
        lon: 'Longitude (°)',
        notes: 'Notes',
        save: 'Save',
        cancel: 'Cancel',
      },
    },
  },
  errors: errorsEn,
};

export const portsTr: typeof portsEn = {
  picker: {
    placeholder: 'Liman, UN/LOCODE ya da ülke ara',
    freeText: 'Liman listesinde yok: metin olarak kalır',
    pickFromList: 'Listeden bir liman seçin',
    company: 'şirket limanı',
  },
  approx: 'Mesafeler yaklaşıktır; seyir planlaması için değildir.',
  attribution: 'Deniz yolları: {{network}} · Limanlar: {{ports}}',
  source: {
    manual: 'Elle',
    imported: 'İçe aktarılan',
    computed: 'Hesaplanan',
    estimate: 'Tahmin',
  },
  route: {
    title: 'Rota ve Mesafeler',
    description: 'Seferin limanları, bacak mesafeleri ve liman sırası önerisi',
    card: 'Sefer Rotası',
    columns: {
      no: '#',
      port: 'Liman',
      country: 'Ülke',
      leg: 'Bacak (NM)',
      source: 'Kaynak',
      cumulative: 'Kümülatif (NM)',
      actions: 'İşlemler',
    },
    role: { from: 'Kalkış', via: 'Uğrak', to: 'Varış' },
    total: 'Toplam',
    totalDistance: 'Toplam Mesafe',
    planned: 'Planlanan Mesafe',
    difference: 'Fark',
    legs: 'Bacak',
    storedLegs: 'Mesafe tablosundan',
    computedLegs: 'Şimdi tahmin edilen',
    noCode: 'Liman listesinde yok',
    noRoute: 'Deniz yolu yok',
    addPort: 'Uğrak limanı ekle',
    add: 'Ekle',
    moveUp: '{{port}} yukarı',
    moveDown: '{{port}} aşağı',
    remove: '{{port}} kaldır',
    save: 'Rotayı Kaydet',
    saved: 'Rota kaydedildi.',
    discard: 'Vazgeç',
    unsaved: 'Rotada kaydedilmemiş değişiklik var.',
    suggest: 'Sıra Öner',
    suggestTitle: 'Sıra Önerisi',
    suggestCurrent: 'Mevcut sıra',
    suggestNew: 'Önerilen sıra',
    saving: '{{nm}} NM daha kısa',
    apply: 'Sırayı Uygula',
    applied: 'Önerilen sıra uygulandı. Saklamak için rotayı kaydedin.',
    alreadyBest: 'Mevcut sıra zaten en kısası.',
    missingCode: 'Sıra önerisi için her limanın listede bir kodu olmalı.',
    nothing: 'Sıraları karşılaştırmak için en az iki uğrak limanı ekleyin.',
    noRouteSuggest: 'Bir bacağın deniz yolu yok; sıra karşılaştırılamıyor.',
    heuristic: 'Çok limanda yaklaşık (en yakın komşu + 2-opt).',
    usePlanned: 'Planlanan Mesafeye Aktar',
    plannedSaved: 'Planlanan mesafe güncellendi.',
    addToTable: 'Mesafe tablosuna kaydet',
    addedToTable: 'Mesafe tablosuna kaydedildi.',
    officeNote: 'Rota gemide planlanır; ofis burada görür.',
    loading: 'Liman verisi yükleniyor…',
    dataError: 'Liman verisi yüklenemedi.',
    summary: 'Özet',
    legOf: '{{from}} → {{to}}',
  },
  distances: {
    title: 'Liman Mesafe Kütüphanesi',
    description: 'Şirketin limandan limana mesafe tablosu',
    tabs: { distances: 'Mesafeler', ports: 'Şirket Limanları' },
    search: 'Liman ya da kod ara',
    table: 'Mesafe tablosu',
    columns: {
      from: 'Kalkış',
      to: 'Varış',
      distance: 'Mesafe (NM)',
      source: 'Kaynak',
      updated: 'Güncelleme',
      note: 'Not',
      actions: 'İşlemler',
    },
    add: 'Mesafe Ekle',
    import: 'CSV İçe Aktar',
    emptyTitle: 'Henüz mesafe yok',
    emptyText:
      'Girdiğiniz ya da içe aktardığınız mesafeler burada saklanır ve hesaplanan tahminlerden önce kullanılır.',
    emptyShip: 'Mesafe tablosunu ofis tutar; o zamana kadar bacaklar gemide tahmin edilir.',
    noMatch: 'Aramaya uyan mesafe yok',
    error: 'Mesafeler yüklenemedi',
    showing: '{{total}} kayıttan {{from}}–{{to}}',
    pagination: 'Mesafe sayfaları',
    actionsOf: '{{pair}} işlemleri',
    edit: 'Düzelt',
    history: 'Geçmiş',
    remove: 'Kaldır',
    recompute: 'Yeniden hesapla',
    removed: 'Mesafe kaldırıldı.',
    saved: 'Mesafe kaydedildi.',
    dialog: {
      title: 'Mesafe Ekle',
      editTitle: 'Mesafeyi Düzelt',
      from: 'Kalkış',
      to: 'Varış',
      distance: 'Mesafe (NM)',
      note: 'Not',
      compute: 'Tahmin Et',
      computed: 'Tahmin: {{nm}} NM',
      saveManual: 'Kaydet',
      saveComputed: 'Tahmini Kaydet',
      cancel: 'Vazgeç',
      manualHint: 'Elle girilen mesafe her zaman hesaplananın önüne geçer.',
    },
    historyDialog: {
      title: 'Değişiklik Geçmişi',
      empty: 'Kayıtlı değişiklik yok.',
      insert: 'Eklendi',
      update: 'Güncellendi',
      delete: 'Kaldırıldı',
      by: '{{name}} tarafından',
      close: 'Kapat',
      fields: {
        distance_nm: 'Mesafe',
        source: 'Kaynak',
        note: 'Not',
        network_source: 'Ağ',
        port_a: 'Liman A',
        port_b: 'Liman B',
        deleted_at: 'Kaldırıldı',
      },
    },
    importDialog: {
      title: 'Mesafeleri İçe Aktar',
      help: 'from, to, distance_nm ve isteğe bağlı not sütunlu CSV; virgül, noktalı virgül ya da sekmeyle ayrılmış; başlık satırı isteğe bağlı. Liman kodları UN/LOCODE ya da liman listesindeki kodlardır. Elle girilen mesafeler korunur.',
      file: 'CSV dosyası',
      text: 'Satırlar',
      preview: 'Önizle',
      apply: 'İçe Aktar',
      result: '{{rows}} satır: {{inserted}} yeni, {{updated}} güncellenen, {{skipped}} atlanan',
      issues: 'Bu satırları düzeltip yeniden önizleyin:',
      line: 'Satır {{line}}',
      skipped: 'Atlanan satırlar',
      manualKept: 'Satır {{line}}: elle girilen mesafe korundu',
      unchanged: 'Satır {{line}}: değişmedi',
      done: 'İçe aktarım tamamlandı.',
    },
    ports: {
      add: 'Liman Ekle',
      search: 'Kod ya da ad ara',
      table: 'Şirket limanları',
      columns: {
        code: 'Kod',
        name: 'Ad',
        country: 'Ülke',
        position: 'Konum',
        euEea: 'AB / AEA',
        notes: 'Not',
        actions: 'İşlemler',
      },
      emptyTitle: 'Şirket limanı yok',
      emptyText: 'Liman listesinde olmayan terminal, rıhtım ya da demir yerlerini ekleyin.',
      yes: 'Evet',
      no: 'Hayır',
      saved: 'Liman kaydedildi.',
      removed: 'Liman kaldırıldı.',
      actionsOf: '{{code}} işlemleri',
      dialog: {
        title: 'Şirket Limanı Ekle',
        editTitle: 'Şirket Limanını Düzelt',
        code: 'Kod',
        codeHint: 'UN/LOCODE biçimi: ilk iki harf ülke',
        name: 'Ad',
        country: 'Ülke kodu',
        lat: 'Enlem (°)',
        lon: 'Boylam (°)',
        notes: 'Not',
        save: 'Kaydet',
        cancel: 'Vazgeç',
      },
    },
  },
  errors: errorsTr,
};
